/* eslint-disable react/jsx-key */
import React from 'react';
import ActionDataCell from './ActionDataCell';
import TextDataCell from './TextDataCell';
import FavouriteDataCell from './FavouriteDataCell';
import NumberDataCell from './NumberDataCell';
import DateDataCell from './DateDataCell';
import CurrencyDataCell from './CurrencyDataCell';
import { gridBorder, gridCell } from './styles';
import DataCell from './DataCell';
import ImageDataCell from './ImageDataCell';
import CustomCell from './CustomCell';
import DateTimeDataCell from './DateTimeDataCell';
import ScrollbarCell from './ScrollbarCell';
import TextInputCell from './TextInputCell';
import SelectCell from './SelectCell';

const cell = {
    ...gridBorder,
    ...gridCell
};

const DataRow = ({
    rowKey,
    rowIds = [],
    definitions,
    data,
    index,
    onActionClick,
    onFavouriteClick,
    primaryColumn,
    onPrimaryColumnClick,
    onActionIconLoad,
    onActionToolTip,
    onCustomClick,
    scrollPxH,
    isHighlighted,
    onHover,
    dateFormat,
    timeFormat,
    locale,
    currency,
    onCheck,
    scrollPxV,
    onScroll,
    maxVisibleLength,
    scrollableLength,
    gutterOffset,
    onSizeChange,
    onKeyPress,
    onTick,
    actionIconSize,
    rowHeight,
    maxVisibleRows,
    deleteRowSelected,
    addToDelete,
    highlightedIdx,
    highlightOnClick,
    setHighlightedIdx,
    onCustomCellStyle
}) => {
    // Produce an empty row when no data available
    if (!data) {
        return (
            <tr>
                {definitions.map((d, i) => (
                    <DataCell
                        key={i}
                        rowIndex={index}
                        scrollPxV={scrollPxV}
                        scrollPxH={scrollPxH}
                        isSticky={d.isSticky}
                        style={{
                            ...cell,
                            width: d.width,
                            height: rowHeight,
                            ...d.style
                        }}
                    />
                ))}
            </tr>
        );
    }

    // find status column
    const columns = definitions.filter((d) => d.isStatus === true);
    if (columns.length > 1)
        throw new Error('Status column defined more than once');

    const statusColumn = columns[0] ?? {};

    const actionCellCount = definitions?.filter(
        (d) => d.type === 'action' || d.type === 'favourite'
    )?.length;

    const hasRestoreAction =
        definitions?.filter(
            (d) => d.type === 'action' && d.accessor.toLowerCase() === 'restore'
        )?.length > 0;

    const buildRowAttributes = () => {
        const retVal = {};

        for (const key in data) {
            if (!rowIds.includes(key)) continue;
            retVal[key] = data[key];
        }

        return retVal;
    };

    const customCellStyle = (data, cellDef) => {
        if (onCustomCellStyle && !isHighlighted) {
            return { ...onCustomCellStyle(data, cellDef) }
        } else {
            return {}
        }
    };

    return (
        <tr key={rowKey} id={rowKey} {...buildRowAttributes()}>
            {definitions.map((d, i) => {
                let cellComponent = null;
                switch (d.type) {
                    case 'action':
                        cellComponent = (
                            <ActionDataCell
                                key={`action${i}`}
                                rowIndex={index}
                                colIndex={i}
                                actionCount={actionCellCount}
                                accessor={d.accessor}
                                onActionClick={onActionClick}
                                data={data}
                                statusAccessor={statusColumn.accessor}
                                onActionIconLoad={onActionIconLoad}
                                onActionToolTip={onActionToolTip}
                                scrollPxH={scrollPxH}
                                scrollPxV={scrollPxV}
                                isSticky={d.isSticky}
                                style={{ ...d.style, ...customCellStyle(data, d) }}
                                onHover={onHover}
                                isHighlighted={isHighlighted}
                                onCheck={onCheck}
                                component={d.component}
                                actionIconSize={actionIconSize}
                                iconMarginTop={hasRestoreAction ? 4 : 0}
                                rowHeight={rowHeight}
                                deleteRowSelected={deleteRowSelected}
                                addToDelete={addToDelete}
                                highlightedIdx={highlightedIdx}
                                setHighlightedIdx={setHighlightedIdx}
                                highlightOnClick={highlightOnClick}
                            />
                        );
                        break;
                    case 'favourite':
                        cellComponent = (
                            <FavouriteDataCell
                                key={`fav${i}`}
                                rowIndex={index}
                                colIndex={i}
                                actionCount={actionCellCount}
                                data={data}
                                onFavouriteClick={onFavouriteClick}
                                scrollPxH={scrollPxH}
                                scrollPxV={scrollPxV}
                                isSticky={d.isSticky}
                                onHover={onHover}
                                isHighlighted={isHighlighted}
                                actionIconSize={actionIconSize}
                                rowHeight={rowHeight}
                                deleteRowSelected={deleteRowSelected}
                                highlightedIdx={highlightedIdx}
                                setHighlightedIdx={setHighlightedIdx}
                                highlightOnClick={highlightOnClick}
                            />
                        );
                        break;
                    case 'text':
                        cellComponent = (
                            <TextDataCell
                                key={`text${i}`}
                                rowIndex={index}
                                data={data}
                                accessor={d.accessor}
                                primaryColumn={primaryColumn}
                                onPrimaryColumnClick={onPrimaryColumnClick}
                                scrollPxH={scrollPxH}
                                scrollPxV={scrollPxV}
                                isSticky={d.isSticky}
                                style={{ ...d.style, ...customCellStyle(data, d) }}
                                onHover={onHover}
                                isHighlighted={isHighlighted}
                                rowHeight={rowHeight}
                                deleteRowSelected={deleteRowSelected}
                                highlightedIdx={highlightedIdx}
                                setHighlightedIdx={setHighlightedIdx}
                                highlightOnClick={highlightOnClick}
                            />
                        );
                        break;
                    case 'number':
                        cellComponent = (
                            <NumberDataCell
                                key={`num${i}`}
                                rowIndex={index}
                                data={data}
                                accessor={d.accessor}
                                scrollPxH={scrollPxH}
                                scrollPxV={scrollPxV}
                                isSticky={d.isSticky}
                                style={{ ...d.style, ...customCellStyle(data, d) }}
                                onHover={onHover}
                                isHighlighted={isHighlighted}
                                locale={locale}
                                rowHeight={rowHeight}
                                deleteRowSelected={deleteRowSelected}
                                highlightedIdx={highlightedIdx}
                                setHighlightedIdx={setHighlightedIdx}
                                highlightOnClick={highlightOnClick}
                            />
                        );
                        break;
                    case 'currency':
                        cellComponent = (
                            <CurrencyDataCell
                                key={`curr${i}`}
                                rowIndex={index}
                                data={data}
                                accessor={d.accessor}
                                scrollPxH={scrollPxH}
                                scrollPxV={scrollPxV}
                                isSticky={d.isSticky}
                                style={{ ...d.style, ...customCellStyle(data, d) }}
                                onHover={onHover}
                                isHighlighted={isHighlighted}
                                locale={locale}
                                currency={currency}
                                rowHeight={rowHeight}
                                deleteRowSelected={deleteRowSelected}
                                highlightedIdx={highlightedIdx}
                                setHighlightedIdx={setHighlightedIdx}
                                highlightOnClick={highlightOnClick}
                            />
                        );
                        break;
                    case 'date':
                        cellComponent = (
                            <DateDataCell
                                key={`date${i}`}
                                rowIndex={index}
                                data={data}
                                accessor={d.accessor}
                                scrollPxH={scrollPxH}
                                scrollPxV={scrollPxV}
                                isSticky={d.isSticky}
                                style={{ ...d.style, ...customCellStyle(data, d) }}
                                onHover={onHover}
                                isHighlighted={isHighlighted}
                                dateFormat={dateFormat}
                                rowHeight={rowHeight}
                                deleteRowSelected={deleteRowSelected}
                                highlightedIdx={highlightedIdx}
                                setHighlightedIdx={setHighlightedIdx}
                                highlightOnClick={highlightOnClick}
                            />
                        );
                        break;
                    case 'datetime':
                        cellComponent = (
                            <DateTimeDataCell
                                key={`datetime${i}`}
                                rowIndex={index}
                                data={data}
                                accessor={d.accessor}
                                scrollPxH={scrollPxH}
                                scrollPxV={scrollPxV}
                                isSticky={d.isSticky}
                                style={{ ...d.style, ...customCellStyle(data, d) }}
                                onHover={onHover}
                                isHighlighted={isHighlighted}
                                dateFormat={dateFormat}
                                timeFormat={timeFormat}
                                rowHeight={rowHeight}
                                deleteRowSelected={deleteRowSelected}
                                highlightedIdx={highlightedIdx}
                                setHighlightedIdx={setHighlightedIdx}
                                highlightOnClick={highlightOnClick}
                            />
                        );
                        break;
                    case 'image':
                        cellComponent = (
                            <ImageDataCell
                                key={`image${i}`}
                                rowIndex={index}
                                data={data}
                                accessor={d.accessor}
                                scrollPxH={scrollPxH}
                                scrollPxV={scrollPxV}
                                isSticky={d.isSticky}
                                style={{ ...d.style, ...customCellStyle(data, d) }}
                                onHover={onHover}
                                isHighlighted={isHighlighted}
                                rowHeight={rowHeight}
                                deleteRowSelected={deleteRowSelected}
                                highlightedIdx={highlightedIdx}
                                setHighlightedIdx={setHighlightedIdx}
                                highlightOnClick={highlightOnClick}
                            />
                        );
                        break;
                    case 'custom':
                        cellComponent = (
                            <CustomCell
                                key={`custom${i}`}
                                rowIndex={index}
                                accessor={d.accessor}
                                accessorDisabled={d.accessorDisabled}
                                customType={d.customType}
                                onCustomClick={onCustomClick}
                                data={data}
                                scrollPxH={scrollPxH}
                                scrollPxV={scrollPxV}
                                isSticky={d.isSticky}
                                style={{ ...d.style, ...customCellStyle(data, d) }}
                                onHover={onHover}
                                isHighlighted={isHighlighted}
                                component={d.component}
                                rowHeight={rowHeight}
                                deleteRowSelected={deleteRowSelected}
                                highlightedIdx={highlightedIdx}
                                setHighlightedIdx={setHighlightedIdx}
                                highlightOnClick={highlightOnClick}
                            />
                        );
                        break;

                    case 'input':
                        cellComponent = (
                            <TextInputCell
                                key={`input${i}`}
                                rowIndex={index}
                                data={data}
                                accessor={d.accessor}
                                scrollPxH={scrollPxH}
                                scrollPxV={scrollPxV}
                                isSticky={d.isSticky}
                                style={{ ...d.style, ...customCellStyle(data, d) }}
                                onHover={onHover}
                                isHighlighted={isHighlighted}
                                onKeyPress={onKeyPress}
                                rowHeight={rowHeight}
                                deleteRowSelected={deleteRowSelected}
                                highlightedIdx={highlightedIdx}
                                setHighlightedIdx={setHighlightedIdx}
                                highlightOnClick={highlightOnClick}
                            />
                        );
                        break;

                    case 'select':
                        cellComponent = (
                            <SelectCell
                                key={`select${i}`}
                                rowIndex={index}
                                data={data}
                                accessor={d.accessor}
                                scrollPxH={scrollPxH}
                                scrollPxV={scrollPxV}
                                isSticky={d.isSticky}
                                style={{ ...d.style, ...customCellStyle(data, d) }}
                                onHover={onHover}
                                isHighlighted={isHighlighted}
                                onTick={onTick}
                                disabled={d.disabled}
                                reverseElements={d.reverseElements}
                                gap={d.tickboxGap}
                                size={d.tickboxSize}
                                tickboxField={d.tickboxField}
                                rowHeight={rowHeight}
                                deleteRowSelected={deleteRowSelected}
                                highlightedIdx={highlightedIdx}
                                setHighlightedIdx={setHighlightedIdx}
                                highlightOnClick={highlightOnClick}
                            />
                        );
                        break;

                    default:
                        cellComponent = (
                            <TextDataCell
                                key={`text${i}`}
                                rowIndex={index}
                                data={data}
                                accessor={d.accessor}
                                scrollPxH={scrollPxH}
                                scrollPxV={scrollPxV}
                                isSticky={d.isSticky}
                                style={{ ...d.style, ...customCellStyle(data, d) }}
                                onHover={onHover}
                                isHighlighted={isHighlighted}
                                rowHeight={rowHeight}
                                deleteRowSelected={deleteRowSelected}
                                highlightedIdx={highlightedIdx}
                                setHighlightedIdx={setHighlightedIdx}
                                highlightOnClick={highlightOnClick}
                            />
                        );
                }
                return cellComponent;
            })}
            {index === 0 && (
                <ScrollbarCell
                    scrollPxV={scrollPxV}
                    scrollPxH={scrollPxH}
                    onScroll={onScroll}
                    maxVisibleLength={maxVisibleLength}
                    scrollableLength={scrollableLength}
                    gutterOffset={gutterOffset}
                    onSizeChange={onSizeChange}
                    maxVisibleRows={maxVisibleRows}
                    highlightedIdx={highlightedIdx}
                    setHighlightedIdx={setHighlightedIdx}
                    highlightOnClick={highlightOnClick}
                />
            )}
        </tr>
    );
};

export default DataRow;
